import { tools } from "../data/tools";
import { isLocale } from "../data/locales";
import type { Locale, ToolId } from "../types";

export type StaticPageId = "privacy" | "terms" | "contact" | "extension" | "pricing";

export type RouteResolution =
  | { kind: "home"; locale: Locale }
  | { kind: "tool"; locale: Locale; toolId: ToolId }
  | { kind: "static"; locale: Locale; page: StaticPageId }
  | { kind: "not-found"; locale: Locale };

const staticPages: StaticPageId[] = ["privacy", "terms", "contact", "extension", "pricing"];

export function localePrefix(locale: Locale): string {
  return locale === "en" ? "" : `/${locale}`;
}

export function toolPath(toolId: ToolId, locale: Locale): string {
  return `${localePrefix(locale)}/tools/${toolId}`;
}

export function staticPath(page: StaticPageId, locale: Locale): string {
  return `${localePrefix(locale)}/${page}`;
}

export function homePath(locale: Locale): string {
  return locale === "en" ? "/" : `/${locale}`;
}

export function resolveRoute(pathname: string, browserLocale: Locale = "en"): RouteResolution {
  const normalized = normalizePath(pathname);
  const firstSegment = normalized.split("/")[1] || "";
  const hasExplicitLocale = isLocale(firstSegment) && firstSegment !== "en";
  const locale: Locale = hasExplicitLocale ? firstSegment : browserLocale;
  const path = hasExplicitLocale ? normalized.replace(new RegExp(`^/${locale}(?=/|$)`), "") || "/" : normalized;

  if (path === "/") {
    return { kind: "home", locale };
  }

  const toolMatch = path.match(/^\/tools\/([^/]+)$/);
  if (toolMatch) {
    const toolId = toolMatch[1] as ToolId;
    if (tools.some((tool) => tool.id === toolId)) {
      return { kind: "tool", locale, toolId };
    }
  }

  const staticMatch = path.match(/^\/([^/]+)$/);
  if (staticMatch && staticPages.includes(staticMatch[1] as StaticPageId)) {
    return { kind: "static", locale, page: staticMatch[1] as StaticPageId };
  }

  return { kind: "not-found", locale };
}

export function alternatePath(pathname: string, targetLocale: Locale): string {
  const route = resolveRoute(pathname);

  if (route.kind === "tool") {
    return toolPath(route.toolId, targetLocale);
  }

  if (route.kind === "static") {
    return staticPath(route.page, targetLocale);
  }

  return homePath(targetLocale);
}

function normalizePath(pathname: string): string {
  const withoutQuery = pathname.split("?")[0]?.split("#")[0] || "/";
  const trimmed = withoutQuery.replace(/\/+$/, "");
  return trimmed || "/";
}
